/**
 * Where to send someone once they have signed in.
 *
 * `ProtectedRoute` puts the page they were trying to reach in the query
 * string, and Clerk's components take it from there. The name is Clerk's own
 * (`redirect_url`), not one of ours, so the value survives the round trip
 * through Clerk's hosted pages.
 */
export const REDIRECT_PARAM = "redirect_url";

/** Signed in with nowhere particular to go: the front page, which is the
 *  welcome page inside the app's own chrome — sidebar, directory, the lot. */
export const DEFAULT_REDIRECT = "/";

/**
 * Only ever return a path on this site.
 *
 * A sign-in page that forwards to whatever the query string says is a phishing
 * primitive: `/sign-in?redirect_url=https://evil.example` is a link that looks
 * like ours and ends somewhere else. Anything that is not a single-slash
 * relative path — a protocol-relative `//host`, a backslash some parsers treat
 * as a slash, an absolute URL to anywhere else — falls back to the default.
 *
 * An absolute URL to *this* origin is reduced to its path rather than thrown
 * away. Clerk rewrites the destination that way when you follow "Already have
 * an account?" out of the sign-up card, and refusing it lost the page somebody
 * was on their way to. Same origin only, and compared after parsing rather
 * than by prefix, since a prefix test says yes to `https://ours.example.evil`.
 */
export function safeRedirect(value: string | null | undefined): string {
  if (!value) return DEFAULT_REDIRECT;
  const path = value.trim();
  if (!path.startsWith("/")) {
    if (typeof window === "undefined") return DEFAULT_REDIRECT;
    try {
      const url = new URL(path);
      if (url.origin !== window.location.origin) return DEFAULT_REDIRECT;
      return safeRedirect(url.pathname + url.search);
    } catch {
      return DEFAULT_REDIRECT;
    }
  }
  if (path.startsWith("//") || path.startsWith("/\\")) return DEFAULT_REDIRECT;
  return path;
}

/**
 * The pages with nothing to show without an account.
 *
 * Everything else on this site reads signed out, at preview tier. These are
 * named one at a time rather than defaulted, because "everything is open
 * unless somebody remembered" is how a paid-only page ends up public — and
 * `router.tsx` refuses to declare an account-only route that is not in here,
 * so the list cannot quietly fall behind.
 */
export const ACCOUNT_ONLY = [
  "/apply",
  "/profile",
  "/billing",
  // The page listing *your* legacies. `/organizations/12` is a legacy's own
  // page, which anybody may read — which is why these are exact paths and not
  // prefixes.
  "/organizations",
  "/resources/mine",
  "/account",
];

/** The one subtree that is account-only all the way down. */
export const ACCOUNT_ONLY_TREES = ["/admin"];

/** Whether a path needs an account. */
export function isAccountOnly(pathname: string): boolean {
  return (
    ACCOUNT_ONLY.includes(pathname) ||
    ACCOUNT_ONLY_TREES.some(
      (path) => pathname === path || pathname.startsWith(`${path}/`)
    )
  );
}

/**
 * Where signing out from `pathname` should land.
 *
 * On the page they were reading, which is the point — except when that page
 * needs an account, where staying put means an immediate bounce to the
 * sign-in form. Signing out and being asked to sign in is the opposite of
 * what was asked for.
 */
export function afterSignOut(pathname: string): string {
  return isAccountOnly(pathname) ? "/" : pathname;
}
